/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Hyunsu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jun 02 2023
 **/

import { useAtom } from "jotai";
import { useState } from "react";

import { _T, _P } from "@rfice/language";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { useModal, useToast } from "@/frameworks/designStuff/components";
import { EventHandler } from "@rfice/eventhandler";

import { useBlock, useFriend, useMyRoom, usePeople, useUsers } from "@/frameworks/core/API";
import { useChatRoomActions } from "@/frameworks/core/chat";
import { APIErrorCode, EventID } from "@/frameworks/core/commons";
import { useNetworkCommand } from "@/frameworks/core/network";
import { useUnitySystemActions } from "@/frameworks/core/unity";
import { usePersistActions } from "@/frameworks/core/persistence";
import { useConfirm } from "@/frameworks/core/hooks";
import { BlockUsers, Friends, GroupPeople } from "./atom";
import { usePhoneFavorite } from "../usePhoneCall";
import { useSidebarActions } from "../useSidebar";

const LOGTAG = "#useFriendHandlers#";

export default function useFriendHandlers() {
	const dispatch = usePersistActions();
	const favorite = usePhoneFavorite();
	const friendAPI = useFriend();
	const blockAPI = useBlock();
	const peopleAPI = usePeople({
		onSuccess(name, result) {
			if (name === "list") {
				const peopleData = result.data as WebServices.People.List.Data;
				const changeUserData = Object.fromEntries(peopleData?.map((value) => [value.groupId, value.users]));
				setGroupPeoples(changeUserData);
			}
		}
	});

	const { go } = useUnitySystemActions();
	const userAPI = useUsers();
	const [, setBlockUsers] = useAtom(BlockUsers);
	const [friends, setFriends] = useAtom(Friends);
	const sidebarActions = useSidebarActions();
	const [groupPeoples, setGroupPeoples] = useAtom(GroupPeople);
	const myRoomAPI = useMyRoom();
	const chatRoomActions = useChatRoomActions();
	const toast = useToast();
	const network = useNetworkCommand();
	const [userDetailData, setUserDetailData] = useState<WebServices.User.UserInfo | undefined>();
	const { confirm } = useConfirm();
	const modal = useModal();

	const updateUserDetailByAPI = async (userID: string) => {
		const infoUser = dispatch.users.get_user(userID);
		if (!infoUser) return;
		setUserDetailData(infoUser as WebServices.User.UserInfo);
	};

	const handleBlockToggle = ({ userID, isBlocked }: { userID: string; isBlocked: boolean }) => {
		if (isBlocked) handleBlockRelease(userID);
		else handleBlockRequest(userID);
	};

	const handleBlockRequest = async (userID: string) => {
		await blockAPI.request.execute({ userId: userID });

		await peopleAPI.list.execute();
		const user = await userAPI.inquiry.execute({ userId: userID });
		const selectUser = friends?.find((item) => item.userId === userID);

		setBlockUsers((prev) => {
			// 클라에서 상태를 관리할때 block은 colorProfile 가 필수 요소여서 user api 통해서 해당 값 가져옴
			if (!prev) return [];
			if (!selectUser) return prev;

			return [
				...prev,
				{
					activeStatus: selectUser.activeStatus,
					colorProfile: user.data?.colorProfile as Avatar.ProfileColor,
					createdTime: selectUser.createdTime,
					name: selectUser.name,
					profileImagePath: "",
					shortDescription: selectUser.shortDescription,
					userId: selectUser.userId,
					username: selectUser.username
				}
			];
		});

		setFriends((prev) => prev?.filter((item) => item.userId !== userID) || []);
	};

	const handleBlockRelease = async (userID: string) => {
		await blockAPI.release.execute({ userId: userID });

		setBlockUsers((prev) => prev?.filter((item) => item.userId !== userID) || []);

		const peoples = await peopleAPI.list.execute();

		if (peoples.data) {
			const changeUserData = Object.fromEntries(peoples.data?.map((value) => [value.groupId, value.users]));
			setGroupPeoples(changeUserData);
		}
	};

	const handleAddFriend = async (userID: string, group?: { groupName: string; groupID: string }) => {
		try {
			if (group) {
				await friendAPI.add.execute({ userId: userID });
				const changeData = groupPeoples[group.groupID].map((item) => {
					if (item.userId === userID) {
						return { ...item, friend: !item.friend };
					}
					return item;
				});
				setGroupPeoples((prev) => {
					return {
						...prev,
						[group.groupID]: [...changeData]
					};
				});
				const friendList = await friendAPI.friends.execute();
				setFriends((prev) => {
					if (!prev) return [];
					const addFriend = friendList.data?.filter((item) => item.userId === userID) || [];
					return [...prev, ...addFriend];
				});
				toast.show({
					type: "success",
					message: _P("{{name}} 님과 친구가 되었습니다.", {
						name: friendList.data?.find((item) => item.userId === userID)?.name
					}) as string
				});
			}
		} catch (error) {
			const err = error as RestAPIError;
			if (err.code === "49223") {
				toast.show({ type: "error", message: _T("친구 요청 권한이 없습니다.") });
				return;
			}
			throw Error("friend api error or userID not Found error");
		}
	};

	const handleRemoveFriend = async (userID: string) => {
		if (!peopleAPI.list.result.data) return;

		const usersData = peopleAPI.list.result.data;

		const changeUserData = Object.fromEntries(
			usersData.map((value) => [
				value.groupId,
				value.users.map((item) => {
					return {
						...item,
						friend: false
					};
				})
			])
		);

		setGroupPeoples(changeUserData);

		await friendAPI.remove.execute({ userId: userID });

		handleUpdateFriend(userID);
	};

	const handleUpdateFriend = (userID: string) => {
		setFriends((prev) => {
			return prev?.filter((item) => item.userId !== userID) || [];
		});
	};

	const handleFriendToggle = ({
		followBack,
		userID,
		group = undefined
	}: {
		followBack: boolean;
		userID: string;
		group?: {
			groupName: string;
			groupID: string;
		};
	}) => {
		try {
			if (followBack) handleRemoveFriend(userID);
			else handleAddFriend(userID, group);
			updateUserDetailByAPI(userID);
		} catch (error) {
			throw Error("friend api error or userID not Found error");
		}
	};

	const handleFollowUser = async (userID: string) => {
		try {
			console.debug(LOGTAG, "handleFollowUser", userID);
			await dispatch.users.check_before_follow(userID);
			network.requestLocation(userID);
		} catch (err) {
			const error = err as RestAPIError;
			if (error.code) {
				switch (error.code) {
					case APIErrorCode.FAILED_FOLLOW_SPACE_BY_NONE_AUTHORITY:
						toast.show({ type: "error", message: _T("따라가기 할 수 없는 스페이스 입니다.") });
						return;
					case APIErrorCode.FAILED_FOLLOW_SPACE:
						toast.show({ type: "error", message: _T("따라가기 할 수 없는 스페이스 입니다.") });
						return;
					case APIErrorCode.FAILED_FOLLOW_SPACE_BY_NONE_CONFERENCE:
						toast.show({ type: "error", message: _T("따라가기 할 수 없는 스페이스 입니다.") });
						return;
					case APIErrorCode.NOT_JOINED_SPACE:
						confirm(
							{
								title: error?.data?.name ?? "",
								descriptions: _P("스페이스 입장 시, 가입 처리 됩니다."),
								confirmText: _T("입장하기")
							},
							{
								onConfirm() {
									network.requestLocation(userID);
								},
								onCancel() {
									modal.hideAll();
								}
							}
						);
						return;
					default:
						break;
				}
			} else {
				const _error = error as Error;
				toast.show({ type: "error", message: _error.message });
			}
			throw Error("friend api error or userID not Found error");
		} finally {
			sidebarActions.hideAll();
		}
	};

	const handleFollowHome = async (userID: string) => {
		try {
			const { data } = await userAPI.inquiry.execute({
				userId: userID
			});
			if (!data || !Object.hasOwn(data, "myRoomId")) {
				toast.show({ type: "error", message: _T("유저 정보를 가져오는데 실패했습니다.") });
				return;
			}

			const myRoom = await myRoomAPI.inquiry.execute({
				myRoomId: data.myRoomId
			});

			if (myRoom.data && myRoom.data?.spaceScene) {
				go("userGuesture", {
					spaceType: "MY_ROOM",
					spaceId: data.myRoomId || "",
					spaceScene: myRoom.data.spaceScene
				});
			}
		} catch (error) {
			throw Error("friend api error or myRoomId not Found error");
		} finally {
			sidebarActions.hideAll();
		}
	};

	const handleFavorites = async ({
		active = false,
		userID
	}: {
		active?: boolean;
		callID?: string;
		userID: string;
	}) => {
		if (active) {
			await favorite.delete(userID);
		} else {
			await favorite.add(userID);
		}
	};
	const handleChat = async (id: string, forceOpenType?: "mini" | "fullsize") => {
		try {
			const response = await chatRoomActions.dm.create({ userIDs: [id] });
			if (!response) return;
			chatRoomActions.common.open({ channelID: response.id, type: "DM" });
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.EXIST_SAME_CHAT_MEMBER: {
					const { roomId } = error.data.payload;
					// const DMRoom = chatRoomListActions.getDMRoom(roomId);
					// if (DMRoom?.hide) {
					// 	chatRoomActions.dm.hide({ channelID: roomId, hide: false });
					// }
					chatRoomActions.common.open({ channelID: roomId, type: "DM", forceOpenType });
					break;
				}
			}
		}
	};

	const handleEmitter = (item: WebServices.People.List.User, type?: "blockedByMe" | "friend") => {
		const data = type ? { ...item, friend: !item[type] } : item;
		EventHandler.emit(EventID.OPEN_PEOPLEDETAIL_PANEL, data, item.me);
	};

	const handleSummonFriend = async (userID: string) => {
		try {
			await dispatch.users.check_before_summon(userID);
			toast.show({
				type: "info",
				message: _T("불러오기가 실행되었습니다.")
			});
			network.requestSummonFriend(userID);
		} catch (err) {
			const error = err as RestAPIError;
			if (error.code) {
				switch (error.code) {
					default:
						break;
				}
			} else {
				const _error = error as Error;
				toast.show({ type: "error", message: _error.message });
			}
		}
	};

	return {
		handlers: {
			handleBlockToggle,
			handleBlockRequest,
			handleBlockRelease,
			handleFriendToggle,
			handleAddFriend,
			handleRemoveFriend,
			handleFollowUser,
			handleFollowHome,
			handleFavorites,
			handleChat,
			handleEmitter,
			userDetailData,
			handleSummonFriend
		}
	};
}
